'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface PortfolioItem {
  lotId: string
  title: string
  artist: string
  artistSlug: string
  addedAt: string
  addedPrice: number // Price when added (hammer)
  currentPrice: number // Current estimated value
  medium: string
  year: string
  house: string
  saleDate: string
  imageUrl?: string
}

interface PortfolioContextType {
  items: PortfolioItem[]
  addItem: (item: Omit<PortfolioItem, 'addedAt'>) => void
  removeItem: (lotId: string) => void
  isInPortfolio: (lotId: string) => boolean
  totalValue: number
  totalCost: number
  totalGain: number
  totalGainPercent: number
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined)

// Sample portfolio data for demo
const samplePortfolio: PortfolioItem[] = [
  {
    lotId: 'CHR-2024-1847',
    title: 'Untitled (Devil)',
    artist: 'Jean-Michel Basquiat',
    artistSlug: 'jean-michel-basquiat',
    addedAt: '2024-01-15',
    addedPrice: 42400000,
    currentPrice: 45200000,
    medium: 'Acrylic, oilstick and spray paint on canvas',
    year: '1982',
    house: "Christie's",
    saleDate: 'February 8, 2024',
  },
  {
    lotId: 'SOT-2024-3291',
    title: 'Infinity Nets (TWHOQ)',
    artist: 'Yayoi Kusama',
    artistSlug: 'yayoi-kusama',
    addedAt: '2024-01-20',
    addedPrice: 8900000,
    currentPrice: 9400000,
    medium: 'Acrylic on canvas',
    year: '2006',
    house: "Sotheby's",
    saleDate: 'February 5, 2024',
  },
  {
    lotId: 'CHR-2024-1901',
    title: 'Green Beret',
    artist: 'Amoako Boafo',
    artistSlug: 'amoako-boafo',
    addedAt: '2024-02-01',
    addedPrice: 3400000,
    currentPrice: 3800000,
    medium: 'Oil on canvas',
    year: '2020',
    house: "Christie's",
    saleDate: 'January 28, 2024',
  },
]

export function PortfolioProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<PortfolioItem[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  // Load from localStorage on mount, fall back to sample data
  useEffect(() => {
    const stored = localStorage.getItem('salle-portfolio')
    if (stored) {
      try {
        setItems(JSON.parse(stored))
      } catch {
        setItems(samplePortfolio)
      }
    } else {
      // Use sample data for demo
      setItems(samplePortfolio)
    }
    setIsLoaded(true)
  }, [])

  // Save to localStorage on change
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('salle-portfolio', JSON.stringify(items))
    }
  }, [items, isLoaded])

  const addItem = (item: Omit<PortfolioItem, 'addedAt'>) => {
    if (!items.find(i => i.lotId === item.lotId)) {
      setItems(prev => [...prev, { ...item, addedAt: new Date().toISOString().split('T')[0] }])
    }
  }

  const removeItem = (lotId: string) => {
    setItems(prev => prev.filter(i => i.lotId !== lotId))
  }

  const isInPortfolio = (lotId: string) => {
    return items.some(i => i.lotId === lotId)
  }

  const totalValue = items.reduce((sum, item) => sum + item.currentPrice, 0)
  const totalCost = items.reduce((sum, item) => sum + item.addedPrice, 0)
  const totalGain = totalValue - totalCost
  const totalGainPercent = totalCost > 0 ? (totalGain / totalCost) * 100 : 0

  return (
    <PortfolioContext.Provider value={{
      items,
      addItem,
      removeItem,
      isInPortfolio,
      totalValue,
      totalCost,
      totalGain,
      totalGainPercent,
    }}>
      {children}
    </PortfolioContext.Provider>
  )
}

export function usePortfolio() {
  const context = useContext(PortfolioContext)
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider')
  }
  return context
}
